import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@lab/src/lib/util";
import { Kbd } from "./kbd";
import {
	ariaKeyShortcuts,
	useKeyboardShortcut,
} from "../../hooks/useKeyboardShortcut";

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				default:
					"bg-primary text-primary-foreground hover:bg-primary/90",
				destructive:
					"bg-destructive text-destructive-foreground hover:bg-destructive/90",
				outline:
					"border border-input bg-background hover:bg-accent hover:text-accent-foreground",
				secondary:
					"bg-secondary text-secondary-foreground hover:bg-secondary/80",
				ghost: "hover:bg-accent hover:text-accent-foreground",
				link: "text-primary underline-offset-4 hover:underline",
			},
			size: {
				default: "h-10 px-4 py-2",
				sm: "h-9 rounded-md px-3",
				lg: "h-11 rounded-md px-8",
				icon: "h-10 w-10",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	}
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	/** Optional leading icon, rendered before the children. Ignored when asChild. */
	icon?: React.ReactNode;
	/**
	 * Keyboard shortcut (e.g. "mod+d") that clicks the button and renders a
	 * hint after the children. Ignored when asChild.
	 */
	shortcut?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			size,
			asChild = false,
			icon,
			shortcut,
			children,
			...props
		},
		ref
	) => {
		const Comp = asChild ? Slot : "button";
		const innerRef = React.useRef<HTMLButtonElement>(null);
		React.useImperativeHandle(
			ref,
			() => innerRef.current as HTMLButtonElement
		);
		// Slot requires exactly one child, so icon and shortcut only apply to
		// real buttons. Clicking via ref respects the disabled state natively.
		const supportsExtras = !asChild;
		useKeyboardShortcut(supportsExtras ? shortcut : undefined, () => {
			const node = innerRef.current;
			if (!node) return;
			// Radix marks background content aria-hidden while a modal is open —
			// the shortcut must not click through the overlay.
			if (node.closest('[data-aria-hidden="true"], [aria-hidden="true"]'))
				return;
			node.click();
		});
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={innerRef}
				aria-keyshortcuts={
					supportsExtras && shortcut
						? ariaKeyShortcuts(shortcut)
						: undefined
				}
				{...props}
			>
				{supportsExtras ? (
					<>
						{icon}
						{children}
						{shortcut && (
							<Kbd shortcut={shortcut} aria-hidden="true" />
						)}
					</>
				) : (
					children
				)}
			</Comp>
		);
	}
);
Button.displayName = "Button";

export { Button, buttonVariants };
